// modules
import { get } from 'lodash'
import { createSelector } from 'reselect'

// utils
import addAllValue from 'src/utils/functions/addAllValue'
import { convertDataFields } from 'src/utils/functions/convertDataFields'

const references = (state = {}) => {
  return get(state, ['company', 'references'], {})
}

export const taskStatus$ = createSelector(
  references,
  ({ taskStatus = [] }) => taskStatus
)

// Care portal, revisit

/* --------- selectors --------- */

export const companyPhoneTypesSelector = createSelector(
  references,
  (refs) => refs.phones
)

export const companyEmailTypesSelector = createSelector(
  references,
  (refs) => refs.emails
)

export const companyGenderTypesSelector = createSelector(references, (refs) =>
  refs.genders.map((item) => ({
    ...item,
    key: item.uuid,
    value: item.uuid,
    title: item.refValue
  }))
)

export const companyGenderTypes$ = createSelector(references, (refs) =>
  refs.genders.map(({ refValue, uuid }) => ({
    key: uuid,
    label: refValue,
    value: uuid
  }))
)

export const companyAddressTypesSelector = createSelector(
  references,
  (refs) => refs.addresses
)

export const companyContactTypes = createSelector(
  references,
  (refs) => refs.relationship
)

export const companyMedicalSpecialtiesSelector = createSelector(
  references,
  (refs) => refs.medicalSpecialties
)

export const companyMedicalSpecialtiesSelectorDropdown = createSelector(
  companyMedicalSpecialtiesSelector,
  (items = []) =>
    items.map(({ uuid, refValue }) => ({
      key: uuid,
      value: uuid,
      title: refValue
    }))
)

export const companyDischargeProgramReasonsSelector = createSelector(
  references,
  (refs) => refs.programDischargeReasons
)

export const companyDischargeProgramReasonsSelectorDropdown = createSelector(
  companyDischargeProgramReasonsSelector,
  (programDischargeReasons = []) =>
    programDischargeReasons.map(({ uuid, refValue }) => ({
      key: uuid,
      value: uuid,
      title: refValue
    }))
)

export const companyDischargeProgramReasonsDropdownWithAll$ = createSelector(
  companyDischargeProgramReasonsSelector,
  (programDischargeReasons = []) => addAllValue(programDischargeReasons, true)
)

export const companyPatientGroupsSelector = createSelector(
  references,
  (refs) => refs.patientGroups
)

export const companyLanguageTypesSelector = createSelector(
  references,
  ({ languages }) =>
    languages.map((item) => ({
      ...item,
      title: item.refValue,
      value: item.uuid
    }))
)

export const companyLanguages$ = createSelector(references, ({ languages }) =>
  addAllValue(languages, true)
)

export const companyEnglishUuid$ = createSelector(
  companyLanguageTypesSelector,
  (languages = []) => {
    const englishLanguage = find(languages, { title: 'English' })
    const englishUuid = get(englishLanguage, ['uuid'], null)

    return englishUuid
  }
)

export const companyActivityTypesSelector = createSelector(
  references,
  (refs) => refs.activityTypes
)

export const companyAuditLogTimerSelector = createSelector(
  references,
  (refs) => refs.auditLogTimer
)

export const companyEducationRefSelector = createSelector(
  references,
  (refs) => refs.education
)

export const companyCheckInRefSelector = createSelector(
  references,
  (refs) => refs.checkIn
)

export const companyMeasurementRefSelector = createSelector(
  references,
  (refs) => refs.measurement
)

export const companyChatRefSelector = createSelector(
  references,
  (refs) => refs.chat
)

export const refTableLoading$ = createSelector(
  references,
  (company) => company.refs.loading
)

export const companyBroadcastCategoryList$ = createSelector(
  references,
  (refs) => refs.broadcastCategoryList
)

export const eventTypes$ = createSelector(references, (refs) => refs.eventTypes)

export const enrollProgramType$ = createSelector(
  references,
  (refs) => refs.enrollProgramType
)

export const enrollProgramTypeDropdown$ = createSelector(
  enrollProgramType$,
  (enrollProgramType = []) =>
    enrollProgramType.map((item) => ({
      ...item,
      key: item.uuid,
      value: item.uuid,
      title: item.refValue
    }))
)

export const enrollProgramTypeWithAll$ = createSelector(
  enrollProgramType$,
  (enrollProgramType = []) => addAllValue(enrollProgramType, true)
)

export const medicationSkipReason$ = createSelector(
  references,
  (refs) => refs.medicationSkipReason
)

export const backfillMedication$ = createSelector(
  references,
  (refs) => refs.backfillMedication
)

export const backfillCarePlan$ = createSelector(
  references,
  (refs) => refs.backfillCarePlan
)

export const patientProviderTypes$ = createSelector(
  references,
  (refs) => refs.patientProvider
)

export const patientInsuranceTypes$ = createSelector(
  references,
  (refs) => refs.patientInsurance
)

// export const patientInsuranceTypesFromManagement$ = createSelector(
//   companyReferenceTableMngmnt$,
//   (refs) => refs.patientInsurance
// )

export const patientHoldReason$ = createSelector(
  references,
  (refs) => refs.patientHoldReason
)

export const patientHoldReasonDropdown$ = createSelector(
  patientHoldReason$,
  (patientHoldReason = []) =>
    patientHoldReason.map((item) => ({
      key: item.uuid,
      label: item.refValue,
      title: item.refValue,
      value: item.uuid
    }))
)

export const prognosis$ = createSelector(references, ({ prognosis }) =>
  prognosis.map((item) => ({
    ...item,
    value: item.uuid,
    title: item.refValue
  }))
)

export const companyTelemedicineRefs$ = createSelector(
  references,
  ({ telemedicine }) => telemedicine
)

export const companyRiskLevelRefs$ = createSelector(
  references,
  ({ riskLevel }) => riskLevel
)

export const companyEmergencyPreparednessRiskLevelRefs$ = createSelector(
  references,
  ({ emergencyPreparednessRiskLevel }) => emergencyPreparednessRiskLevel
)

export const companyModeOfTransportationRefs$ = createSelector(
  references,
  ({ modeOfTransportation }) => modeOfTransportation
)

export const companyFileTypeRefs$ = createSelector(
  references,
  (refs) => refs.externalDocumentType
)

export const companyFileTypeRefsDropdown$ = createSelector(
  companyFileTypeRefs$,
  (externalDocumentType) => convertDataFields(externalDocumentType)
)

export const companyFileTypeRefsWithAll$ = createSelector(
  companyFileTypeRefs$,
  (externalDocumentType) => addAllValue(externalDocumentType, true)
)

export const companyAlertQuickCloseReasons$ = createSelector(
  references,
  ({ alertQuickCloseReasons }) => alertQuickCloseReasons
)

export const medicalEquipmentStatuses$ = createSelector(
  references,
  ({ medicalEquipmentStatuses = [] }) => medicalEquipmentStatuses
)

export const medicalEquipmentStatusesDropdown$ = createSelector(
  medicalEquipmentStatuses$,
  (medicalEquipmentStatuses = []) => convertDataFields(medicalEquipmentStatuses)
)

export const medicalEquipmentCategories$ = createSelector(
  references,
  ({ medicalEquipmentCategories = [] }) => medicalEquipmentCategories
)

export const medicalEquipmentCategoriesDropdown$ = createSelector(
  medicalEquipmentCategories$,
  (medicalEquipmentCategories = []) =>
    convertDataFields(medicalEquipmentCategories)
)

export const medicalDeviceTrainingStatus$ = createSelector(
  references,
  ({ medicalDeviceTrainingStatus = [] }) => medicalDeviceTrainingStatus
)

export const medicalDeviceTrainingStatusDropdown$ = createSelector(
  medicalDeviceTrainingStatus$,
  (medicalDeviceTrainingStatus = []) =>
    medicalDeviceTrainingStatus.map(({ refValue: title, uuid: value }) => ({
      title,
      value,
      key: value
    }))
)

export const medicalDeviceTrainingStatusDropdownV5$ = createSelector(
  medicalDeviceTrainingStatus$,
  (medicalDeviceTrainingStatus = []) =>
    medicalDeviceTrainingStatus.map(({ refValue: label, uuid: value }) => ({
      key: value,
      label,
      value
    }))
)

export const medicalDeviceTrainingStatusWithAll$ = createSelector(
  medicalDeviceTrainingStatus$,
  (medicalDeviceTrainingStatus = []) =>
    addAllValue(medicalDeviceTrainingStatus, true)
)

export const medicalDeviceShipmentStatus$ = createSelector(
  references,
  ({ medicalDeviceShipmentStatus = [] }) => medicalDeviceShipmentStatus
)

export const medicalDeviceShipmentStatusDropdown$ = createSelector(
  medicalDeviceShipmentStatus$,
  (medicalDeviceShipmentStatus = []) =>
    medicalDeviceShipmentStatus.map(({ refValue: title, uuid: value }) => ({
      title,
      value,
      key: value
    }))
)

export const medicalDeviceShipmentStatusWithAll$ = createSelector(
  medicalDeviceShipmentStatus$,
  (medicalDeviceShipmentStatus = []) =>
    addAllValue(medicalDeviceShipmentStatus, true)
)

export const telemedicineCallType$ = createSelector(
  references,
  ({ telemedicineCallType = [] }) => telemedicineCallType
)

export const telemedicineCallTypeDropdown$ = createSelector(
  telemedicineCallType$,
  (telemedicineCallType = []) =>
    telemedicineCallType
      .filter(({ enabled }) => enabled)
      .map(({ refValue, uuid, defaultCallDuration }) => ({
        key: uuid,
        value: uuid,
        title: refValue,
        label: refValue,
        defaultCallDuration
      }))
)

export const telemedicineCallTypeDropdownWithAll$ = createSelector(
  telemedicineCallTypeDropdown$,
  (telemedicineCallType = []) => addAllValue(telemedicineCallType)
)

export const telemedicineCallTypesDropdownWithAllV5$ = createSelector(
  telemedicineCallTypeDropdown$,
  (telemedicineCallType = []) => addAllValue(telemedicineCallType)
)

export const taskStatusDropdown$ = createSelector(
  taskStatus$,
  (taskStatus = []) =>
    taskStatus
      .filter(({ enabled }) => enabled)
      .map(({ uuid, refValue }) => ({
        key: uuid,
        value: uuid,
        title: refValue,
        label: refValue
      }))
)

export const taskStatusWithAll$ = createSelector(
  taskStatusDropdown$,
  (taskStatus = []) => addAllValue(taskStatus)
)

// export const taskStatusWithAll$ = createSelector(
//   taskStatus$,
//   (taskStatus = []) =>
//     addAllValue(
//       taskStatus.filter(({ enabled }) => enabled),
//       true
//     )
// )

export const taskType$ = createSelector(
  references,
  ({ taskType = [] }) => taskType
)

export const taskTypeDropdown$ = createSelector(taskType$, (taskType = []) =>
  taskType
    .filter(({ enabled = false }) => enabled)
    .map(({ uuid = '', refValue = '' }) => ({
      key: uuid,
      value: uuid,
      title: refValue
    }))
)

export const taskTypeDropdownV5$ = createSelector(taskType$, (taskType = []) =>
  taskType
    .filter(({ enabled = false }) => enabled)
    .map(({ refValue = '', uuid: value = '' }) => ({
      key: value,
      label: refValue,
      value
    }))
)

export const taskTypeDropdownWithAll$ = createSelector(
  taskType$,
  (taskType = []) => addAllValue(taskType, true)
)

export const taskTypeDropdownWithAllV5$ = createSelector(
  taskType$,
  (taskType = []) => addAllValue(taskType)
)

export const taskAction$ = createSelector(
  references,
  ({ taskAction = [] }) => taskAction
)

export const taskActionDropdown$ = createSelector(
  taskAction$,
  (taskAction = []) =>
    taskAction
      .filter(({ enabled }) => enabled)
      .map(({ uuid, refValue }) => ({
        key: uuid,
        value: uuid,
        title: refValue
      }))
)

export const urgentCareOutcome$ = createSelector(references, (company = {}) =>
  get(company, ['urgentCareOutcome'], []).map(({ uuid, refValue }) => ({
    key: uuid,
    value: uuid,
    title: refValue
  }))
)

export const urgentCareOutcomeV5$ = createSelector(
  references,
  ({ urgentCareOutcome = [] }) =>
    urgentCareOutcome.map(({ uuid, refValue }) => ({
      key: uuid,
      label: refValue,
      value: uuid
    }))
)

export const telemedicineCallOutcomeDropdown$ = createSelector(
  references,
  (company = {}) =>
    get(company, ['telemedicineCallOutcome'], [])
      .filter(({ enabled }) => enabled)
      .map(({ uuid, refValue }) => ({
        key: uuid,
        value: uuid,
        title: refValue
      }))
)

export const companyReferralReasonTypesSelector = createSelector(
  references,
  (refs) => refs.referralReasons
)

export const companyReferralReasonTypesWithNoOption$ = createSelector(
  companyReferralReasonTypesSelector,
  (referralReasons = []) => {
    const NO_REFERRAL_REASON = {
      uuid: 'no-referral-reason',
      refValue: 'No referral reason'
    }
    return [...referralReasons, NO_REFERRAL_REASON]
  }
)

export const companyReferralOutreachStatusesTypesSelector = createSelector(
  references,
  (refs) => refs.referralOutreachStatuses
)

export const actionNotificationTypes$ = createSelector(
  references,
  ({ actionNotification = [] }) => actionNotification
)

export const actionNotificationTypesDropdown$ = createSelector(
  actionNotificationTypes$,
  (actionNotificationTypes = []) =>
    actionNotificationTypes.map(({ refValue, uuid }) => ({
      key: uuid,
      value: uuid,
      title: refValue
    }))
)

export const actionNotificationTypesDropdownV5$ = createSelector(
  actionNotificationTypes$,
  (actionNotificationTypes = []) =>
    actionNotificationTypes
      .filter(({ enabled }) => enabled)
      .map(({ refValue, uuid }) => ({
        key: uuid,
        label: refValue,
        value: uuid
      }))
)

export const actionNotificationTypesDropdownWithAll$ = createSelector(
  actionNotificationTypes$,
  (actionNotificationTypes = []) => addAllValue(actionNotificationTypes, true)
)

export const companyPreventiveTypes$ = createSelector(
  references,
  (refs = {}) => refs?.preventiveTypes || []
)

export const companyPreventiveTypesWithAll$ = createSelector(
  companyPreventiveTypes$,
  (preventiveTypes = []) => addAllValue(preventiveTypes, true)
)

export const accountManagers$ = createSelector(
  references,
  (refs) => refs.accountManager
)

export const companyAccountManagersSelector$ = createSelector(
  references,
  ({ accountManager = [] }) =>
    accountManager.map((item) => ({
      ...item,
      label: item.refValue,
      value: item.uuid
    }))
)

export const appointmentStatuses$ = createSelector(
  references,
  (refs) => refs?.appointmentStatuses || []
)

export const appointmentStatusesDropdown$ = createSelector(
  appointmentStatuses$,
  (appointmentStatuses = []) =>
    appointmentStatuses.map(({ uuid, refValue }) => ({
      key: uuid,
      value: uuid,
      title: refValue,
      label: refValue
    }))
)

export const appointmentStatusesFilteredDropdown$ = createSelector(
  appointmentStatusesDropdown$,
  (appointmentStatuses = []) =>
    appointmentStatuses.filter(({ label = '' }) => !['Deleted'].includes(label))
)

export const appointmentStatusesDropdownWithAll$ = createSelector(
  appointmentStatusesFilteredDropdown$,
  (appointmentStatuses = []) => addAllValue(appointmentStatuses, true)
)
